/**
 * @fileoverview Onboarding wizard for new users.
 * Multi-step conversational flow that guides users through:
 * 1. Company name entry (creates organization with auto-generated slug)
 * 2. Single vs multi-business question
 * 3. Multi-business name entry (if applicable)
 * 4. Vendor name entry (simple list)
 * 5. First invoice upload (queued for processing on the core OCR page)
 *
 * After the user selects and uploads an invoice, onboarding adds the PDF to
 * the OCR queue and redirects to /org/:slug/ocr. The core OCR page
 * (ClaudeOCRBatchQueue) auto-processes the single pending queue item and
 * opens ClaudeOCRReview — giving onboarded users the same review UX as the
 * core upload flow instead of a simplified duplicate.
 *
 * Uses "your company" framing throughout -- no technical jargon.
 * Slug is auto-generated and hidden from users.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * Shared logo header rendered at the top of every onboarding step.
 * @returns {JSX.Element} Logo image
 */
function OnboardingLogo() {
  return (
    <a href="https://cogslog.com" aria-label="COGSLOG home">
      <img
        src="/logos/stacked/cogslog-stacked-tagline-color.svg"
        alt="COGSLOG"
        className="auth-logo"
      />
    </a>
  );
}

/**
 * Generate a URL-safe slug from a company name.
 *
 * @param {string} name - The company name
 * @returns {string} A URL-safe slug
 */
function generateSlug(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Format a list of names with Oxford comma.
 *
 * @param {string[]} names - Array of names
 * @returns {string} Formatted list string
 */
function formatBusinessList(names) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Onboarding wizard component.
 * Guides new users through company, business, vendor, and first invoice setup.
 *
 * @returns {JSX.Element} The onboarding wizard page
 */
function Onboarding() {
  const { needsOnboarding, authenticated, initialized, logout, user } = useAuth();

  // Wizard step management
  const [step, setStep] = useState('company');
  const [stepHistory, setStepHistory] = useState([]);

  // Wizard data — company & business
  const [companyName, setCompanyName] = useState('');
  const [slug, setSlug] = useState('');
  const [isMultiBusiness, setIsMultiBusiness] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [newBusinessName, setNewBusinessName] = useState('');

  // Wizard data — vendors
  const [vendors, setVendors] = useState([]);
  const [newVendorName, setNewVendorName] = useState('');

  // Wizard data — invoice upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');

  // Refs
  const stepHeadingRef = useRef(null);
  const fileInputRef = useRef(null);

  // Wait for auth to fully initialize before making redirect decisions
  if (!initialized) {
    return (
      <div className="app-loading">
        <img
          src="/logos/loader/cogslog-loader-light-md.svg"
          width="48"
          height="108"
          alt="Loading..."
          role="status"
        />
      </div>
    );
  }

  // If user doesn't need onboarding, redirect to org picker
  if (authenticated && !needsOnboarding) {
    return <Navigate to="/" replace />;
  }

  /**
   * Navigate to a new wizard step.
   * @param {string} nextStep - The step to navigate to
   */
  const goToStep = (nextStep) => {
    setStepHistory(prev => [...prev, step]);
    setStep(nextStep);
    setError('');
    setConfirmation('');
  };

  /**
   * Navigate back to the previous wizard step.
   */
  const goBack = () => {
    setStepHistory(prev => {
      const newHistory = [...prev];
      const previousStep = newHistory.pop();
      if (previousStep) {
        setStep(previousStep);
        setError('');
        setConfirmation('');
      }
      return newHistory;
    });
  };

  // ─── Company step handlers ───

  const handleCompanyNameChange = (e) => {
    const name = e.target.value;
    setCompanyName(name);
    setSlug(generateSlug(name));
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    const trimmedName = companyName.trim();
    if (!trimmedName) { setError('Please enter your company name'); return; }
    const generatedSlug = generateSlug(trimmedName);
    if (!generatedSlug) { setError('Please enter a valid company name with letters or numbers'); return; }

    setLoading(true);
    setError('');
    try {
      await api.createOrganization({ name: trimmedName, slug: generatedSlug });
      setSlug(generatedSlug);
      goToStep('business-question');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Business step handlers ───

  const handleSingleBusiness = async () => {
    setLoading(true);
    setError('');
    setIsMultiBusiness(false);
    try {
      await api.createBusiness({ name: companyName.trim() });
      setBusinesses([companyName.trim()]);
      setConfirmation(`Perfect! We've set up ${companyName.trim()} for you.`);
      setTimeout(() => goToStep('vendors'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleBusiness = () => {
    setIsMultiBusiness(true);
    goToStep('multi-business');
  };

  const handleAddBusiness = () => {
    const trimmed = newBusinessName.trim();
    if (!trimmed) return;
    setBusinesses(prev => [...prev, trimmed]);
    setNewBusinessName('');
  };

  const handleBusinessKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddBusiness(); }
  };

  const handleRemoveBusiness = (index) => {
    setBusinesses(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitBusinesses = async () => {
    if (businesses.length === 0) return;
    setLoading(true);
    setError('');
    try {
      for (const name of businesses) {
        await api.createBusiness({ name });
      }
      goToStep('vendors');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Vendor step handlers ───

  const handleAddVendor = () => {
    const trimmed = newVendorName.trim();
    if (!trimmed) return;
    setVendors(prev => [...prev, trimmed]);
    setNewVendorName('');
  };

  const handleVendorKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddVendor(); }
  };

  const handleRemoveVendor = (index) => {
    setVendors(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Submit all vendor names via API, then advance to invoice upload.
   */
  const handleSubmitVendors = async () => {
    if (vendors.length === 0) return;
    setLoading(true);
    setError('');
    try {
      for (const name of vendors) {
        await api.createVendor({ name });
      }
      goToStep('invoice-upload');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipVendors = () => {
    goToStep('invoice-upload');
  };

  // ─── Invoice upload handlers ───

  /**
   * Handle file selection for invoice upload.
   * @param {React.ChangeEvent<HTMLInputElement>} e - File input change event
   */
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) setUploadedFile(file);
  };

  /**
   * Upload the selected file to the OCR processing queue and redirect to
   * the core OCR page, where ClaudeOCRBatchQueue auto-processes the single
   * pending queue item and opens ClaudeOCRReview. Uses window.location.href
   * so the auth + org routing contexts fully re-initialize on arrival.
   */
  const handleUploadAndContinue = async () => {
    if (!uploadedFile) return;
    setLoading(true);
    setError('');
    setUploadProgress('Uploading invoice...');
    try {
      const formData = new FormData();
      formData.append('files', uploadedFile);
      await api.uploadToQueue(formData);
      window.location.href = `/org/${slug}/ocr`;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload invoice. You can skip this step and try later.');
      setUploadProgress('');
      setLoading(false);
    }
  };

  const handleSkipInvoice = () => {
    window.location.href = `/org/${slug}/dashboard`;
  };

  // ─── Step renderers ───

  const renderCompanyStep = () => (
    <div className="onboarding-wizard__step" key="company">
      <div className="onboarding-header">
        <OnboardingLogo />
        <h1 ref={stepHeadingRef} tabIndex={-1}>Welcome to COGSLOG!</h1>
        <p>Let's get your company set up. What's the name of your company?</p>
      </div>

      <form onSubmit={handleCompanySubmit} className="onboarding-form">
        {error && <div className="onboarding-error" role="alert">{error}</div>}
        <div className="onboarding-field">
          <label htmlFor="company-name">Company Name</label>
          <input
            id="company-name"
            type="text"
            value={companyName}
            onChange={handleCompanyNameChange}
            placeholder="e.g. Acme Restaurant Group"
            autoFocus
            disabled={loading}
            aria-invalid={error ? 'true' : undefined}
          />
        </div>
        <button type="submit" className="btn btn-primary onboarding-submit" disabled={loading || !companyName.trim()}>
          {loading ? 'Setting up...' : 'Continue'}
        </button>
      </form>
    </div>
  );

  const renderBusinessQuestionStep = () => (
    <div className="onboarding-wizard__step" key="business-question">
      <div className="onboarding-header">
        <OnboardingLogo />
        <h1 ref={stepHeadingRef} tabIndex={-1}>One more quick question</h1>
        <p>Do you run more than one line of business?</p>
      </div>
      <p className="onboarding-wizard__helper">
        For example, a restaurant and a catering company, or multiple locations with separate ordering
      </p>
      {error && <div className="onboarding-error" role="alert">{error}</div>}
      {confirmation && (
        <div className="onboarding-wizard__confirmation" role="status"><p>{confirmation}</p></div>
      )}
      {!confirmation && (
        <div className="onboarding-wizard__choice-group">
          <button type="button" className="onboarding-wizard__choice-btn" onClick={handleSingleBusiness} disabled={loading}>
            {loading ? 'Setting up...' : 'No, just one'}
          </button>
          <button type="button" className="onboarding-wizard__choice-btn" onClick={handleMultipleBusiness} disabled={loading}>
            Yes, I have multiple
          </button>
        </div>
      )}
    </div>
  );

  const renderMultiBusinessStep = () => (
    <div className="onboarding-wizard__step" key="multi-business">
      <div className="onboarding-header">
        <OnboardingLogo />
        <h1 ref={stepHeadingRef} tabIndex={-1}>What are your lines of business?</h1>
        <p>Add each business or location you'd like to track separately.</p>
      </div>
      {error && <div className="onboarding-error" role="alert">{error}</div>}
      <div className="onboarding-wizard__add-row">
        <input
          type="text"
          value={newBusinessName}
          onChange={(e) => setNewBusinessName(e.target.value)}
          onKeyDown={handleBusinessKeyDown}
          placeholder="e.g. Downtown Location"
          disabled={loading}
          aria-label="Business name"
        />
        <button type="button" className="onboarding-wizard__add-btn" onClick={handleAddBusiness} disabled={loading || !newBusinessName.trim()}>
          Add
        </button>
      </div>
      {businesses.length > 0 && (
        <ul className="onboarding-wizard__business-list" aria-label="Added businesses">
          {businesses.map((name, index) => (
            <li key={index} className="onboarding-wizard__business-item">
              <span>{name}</span>
              <button type="button" className="onboarding-wizard__remove-btn" onClick={() => handleRemoveBusiness(index)} disabled={loading} aria-label={`Remove ${name}`}>
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="btn btn-primary onboarding-submit" onClick={handleSubmitBusinesses} disabled={loading || businesses.length === 0}>
        {loading ? 'Setting up...' : 'Continue'}
      </button>
    </div>
  );

  const renderVendorsStep = () => (
    <div className="onboarding-wizard__step" key="vendors">
      <div className="onboarding-header">
        <OnboardingLogo />
        <h1 ref={stepHeadingRef} tabIndex={-1}>Who are your vendors?</h1>
        <p>Add the suppliers you order from most often. You can always add more later.</p>
      </div>
      {error && <div className="onboarding-error" role="alert">{error}</div>}
      <div className="onboarding-wizard__add-row">
        <input
          type="text"
          value={newVendorName}
          onChange={(e) => setNewVendorName(e.target.value)}
          onKeyDown={handleVendorKeyDown}
          placeholder="e.g. Sysco, US Foods, Restaurant Depot"
          disabled={loading}
          aria-label="Vendor name"
          autoFocus
        />
        <button type="button" className="onboarding-wizard__add-btn" onClick={handleAddVendor} disabled={loading || !newVendorName.trim()}>
          Add
        </button>
      </div>
      {vendors.length > 0 && (
        <ul className="onboarding-wizard__business-list" aria-label="Added vendors">
          {vendors.map((name, index) => (
            <li key={index} className="onboarding-wizard__business-item">
              <span>{name}</span>
              <button type="button" className="onboarding-wizard__remove-btn" onClick={() => handleRemoveVendor(index)} disabled={loading} aria-label={`Remove ${name}`}>
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="onboarding-wizard__actions">
        <button type="button" className="btn btn-primary onboarding-submit" onClick={handleSubmitVendors} disabled={loading || vendors.length === 0}>
          {loading ? 'Saving...' : 'Continue'}
        </button>
        <button type="button" className="btn btn-ghost onboarding-skip" onClick={handleSkipVendors} disabled={loading}>
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderInvoiceUploadStep = () => (
    <div className="onboarding-wizard__step" key="invoice-upload">
      <div className="onboarding-header">
        <OnboardingLogo />
        <h1 ref={stepHeadingRef} tabIndex={-1}>Upload your first invoice</h1>
        <p>We'll use AI to automatically extract vendor, items, and totals from your invoice.</p>
      </div>
      {error && <div className="onboarding-error" role="alert">{error}</div>}
      {uploadProgress && (
        <div className="onboarding-wizard__progress" role="status">
          <img src="/logos/loader/cogslog-loader-light-sm.svg" width="24" height="54" alt="" aria-hidden="true" />
          <p>{uploadProgress}</p>
        </div>
      )}
      {!uploadProgress && (
        <>
          <div
            className="onboarding-wizard__dropzone"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
            tabIndex={0}
            role="button"
            aria-label="Select invoice file"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="onboarding-wizard__dropzone-icon">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {uploadedFile ? (
              <p className="onboarding-wizard__dropzone-text">{uploadedFile.name}</p>
            ) : (
              <p className="onboarding-wizard__dropzone-text">Click to select a PDF or image of an invoice</p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              aria-label="Invoice file"
            />
          </div>
          <div className="onboarding-wizard__actions">
            <button type="button" className="btn btn-primary onboarding-submit" onClick={handleUploadAndContinue} disabled={loading || !uploadedFile}>
              {loading ? 'Uploading...' : 'Upload & Continue'}
            </button>
            <button type="button" className="btn btn-ghost onboarding-skip" onClick={handleSkipInvoice} disabled={loading}>
              Skip for now
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'company': return renderCompanyStep();
      case 'business-question': return renderBusinessQuestionStep();
      case 'multi-business': return renderMultiBusinessStep();
      case 'vendors': return renderVendorsStep();
      case 'invoice-upload': return renderInvoiceUploadStep();
      default: return renderCompanyStep();
    }
  };

  // Steps that should not show the back button
  const noBackSteps = ['company'];

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        {user && (
          <div className="onboarding-user-info">
            <span>Signed in as {user.display_name || user.email}</span>
            <button type="button" onClick={logout} className="onboarding-logout-btn">
              Sign Out
            </button>
          </div>
        )}

        {!noBackSteps.includes(step) && (
          <button type="button" className="onboarding-wizard__back-btn" onClick={goBack}>
            &larr; Back
          </button>
        )}

        <div aria-live="polite">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
